import { API_BASE_URL } from '../utils/constants';
import logger from '../utils/logger';

const CHECK_INTERVAL_MS = 30000;
const HEALTH_TIMEOUT = 5000;
const DEBOUNCE_MS = 2000;

class OnlineDetector {
  constructor() {
    this._isOnline = navigator.onLine;
    this._lastOnlineTimestamp = this._isOnline ? Date.now() : null;
    this._listeners = new Set();
    this._intervalId = null;
    this._debounceTimer = null;
    // Use the top-level /health which is exempt from the global rate limiter.
    // Derive origin from API_BASE_URL (e.g. http://localhost:5000/api -> http://localhost:5000/health)
    // and fall back to /health for relative "/api" base.
    const apiBase = (API_BASE_URL || '').replace(/\/$/, '');
    const healthOrigin = apiBase.replace(/\/api\/?$/, '');
    this._healthCheckUrl = healthOrigin ? `${healthOrigin}/health` : '/health';

    this._handleOnlineBound = this._handleOnline.bind(this);
    this._handleOfflineBound = this._handleOffline.bind(this);
  }

  get isOnline() {
    return this._isOnline;
  }

  get lastOnlineTimestamp() {
    return this._lastOnlineTimestamp;
  }

  start() {
    window.addEventListener('online', this._handleOnlineBound);
    window.addEventListener('offline', this._handleOfflineBound);
    this._startPeriodicCheck();
    logger.info('[OnlineDetector] Started', null, 'OnlineDetector');
  }

  stop() {
    window.removeEventListener('online', this._handleOnlineBound);
    window.removeEventListener('offline', this._handleOfflineBound);
    this._stopPeriodicCheck();
    this._listeners.clear();
    logger.info('[OnlineDetector] Stopped', null, 'OnlineDetector');
  }

  onChange(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  async checkNow() {
    const online = await this._performHealthCheck();
    this._setOnline(online);
    return online;
  }

  _startPeriodicCheck() {
    if (this._intervalId) return;
    this._intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (this._isOnline) {
        this._performHealthCheck().then(online => {
          if (!online) this._setOnline(false);
        });
      }
    }, CHECK_INTERVAL_MS);
  }

  _stopPeriodicCheck() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  async _performHealthCheck() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT);

      const response = await fetch(this._healthCheckUrl, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);
      // 429 means we are online but rate-limited — don't flip to offline
      if (response.status === 429) return true;
      return response.ok;
    } catch {
      return false;
    }
  }

  _handleOnline() {
    this._debounce(() => {
      this._performHealthCheck().then(online => {
        if (online) this._setOnline(true);
      });
    });
  }

  _handleOffline() {
    this._debounce(() => {
      this._setOnline(false);
    });
  }

  _debounce(fn) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(fn, DEBOUNCE_MS);
  }

  _setOnline(online) {
    if (this._isOnline === online) return;
    this._isOnline = online;
    if (online) {
      this._lastOnlineTimestamp = Date.now();
    }
    logger.info(`[OnlineDetector] Status changed: ${online ? 'ONLINE' : 'OFFLINE'}`, null, 'OnlineDetector');
    for (const listener of this._listeners) {
      try { listener(online); } catch {}
    }
  }
}

const onlineDetector = new OnlineDetector();
export default onlineDetector;
